import { useState } from 'react';
import { AsyncBoundary, Drawer, Notice } from '@kollez/design-system';
import {
  useIncidents, useIncident, useDeclareIncident, usePostIncidentUpdate,
  type IncidentSeverity, type IncidentStatus, type IncidentCategory,
} from '@/lib/queries';

/**
 * /platform/incidents (Page 70/N) — first-class incident CRUD.
 *
 * Platform ops declares incidents when user-visible things break. This
 * page lists them, opens a detail drawer with the update timeline, and
 * lets ops declare a new one or post an update / resolve / postmortem.
 * The Health page previously showed a fake "Incident declared" toast —
 * this makes it real.
 */

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  sev1: 'SEV1 · critical',
  sev2: 'SEV2 · high',
  sev3: 'SEV3 · degraded',
  sev4: 'SEV4 · minor',
};

const STATUS_LABEL: Record<IncidentStatus, string> = {
  declared: 'Declared',
  investigating: 'Investigating',
  mitigating: 'Mitigating',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
  postmortem: 'Postmortem',
};

const CATEGORY_LABEL: Record<IncidentCategory, string> = {
  availability: 'Availability',
  data: 'Data',
  security: 'Security',
  ai: 'Nexie / AI',
  payments: 'Payments',
  notifications: 'Notifications',
  assessments: 'Assessments',
  other: 'Other',
};

function sevTone(s: IncidentSeverity): string {
  if (s === 'sev1') return 'badge-danger';
  if (s === 'sev2') return 'badge-warning';
  if (s === 'sev3') return 'badge-info';
  return '';
}

function statusTone(s: IncidentStatus): string {
  if (s === 'resolved' || s === 'postmortem') return 'badge-success';
  if (s === 'monitoring') return 'badge-info';
  if (s === 'declared' || s === 'investigating' || s === 'mitigating') return 'badge-warning';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function durationText(mins?: number): string {
  if (typeof mins !== 'number') return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function PlatformIncidentsPage() {
  const [status, setStatus] = useState<IncidentStatus | ''>('');
  const [severity, setSeverity] = useState<IncidentSeverity | ''>('');
  const [category, setCategory] = useState<IncidentCategory | ''>('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [declaring, setDeclaring] = useState(false);

  const list = useIncidents({ status, severity, category, sortBy: 'startedAt', sortDir: 'desc', limit: 100 });
  const detail = useIncident(openId ?? undefined);
  const declare = useDeclareIncident();
  const postUpdate = usePostIncidentUpdate();

  // Declare form state
  const [dTitle, setDTitle] = useState('');
  const [dCategory, setDCategory] = useState<IncidentCategory>('availability');
  const [dSeverity, setDSeverity] = useState<IncidentSeverity>('sev2');
  const [dPlatformWide, setDPlatformWide] = useState(false);
  const [dServices, setDServices] = useState('');
  const [dPublic, setDPublic] = useState(false);
  const [dMessage, setDMessage] = useState('');

  // Update form state
  const [uStatus, setUStatus] = useState<IncidentStatus>('investigating');
  const [uMessage, setUMessage] = useState('');
  const [uRootCause, setURootCause] = useState('');
  const [uPostmortemUrl, setUPostmortemUrl] = useState('');

  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary;

  const resetDeclareForm = () => {
    setDTitle(''); setDCategory('availability'); setDSeverity('sev2');
    setDPlatformWide(false); setDServices(''); setDPublic(false); setDMessage('');
  };

  const submitDeclare = () => {
    if (dTitle.trim().length < 5 || dMessage.trim().length < 5) return;
    declare.mutate(
      {
        title: dTitle.trim(),
        category: dCategory,
        severity: dSeverity,
        platformWide: dPlatformWide,
        affectedServices: dServices.split(',').map((s) => s.trim()).filter(Boolean),
        publicVisible: dPublic,
        initialMessage: dMessage.trim(),
      },
      {
        onSuccess: () => {
          setDeclaring(false);
          resetDeclareForm();
        },
      },
    );
  };

  const submitUpdate = () => {
    if (!openId || uMessage.trim().length < 5) return;
    postUpdate.mutate(
      {
        id: openId,
        status: uStatus,
        message: uMessage.trim(),
        rootCause: uRootCause.trim() || undefined,
        postmortemUrl: uPostmortemUrl.trim() || undefined,
      },
      {
        onSuccess: () => {
          setUMessage('');
          setURootCause('');
          setUPostmortemUrl('');
        },
      },
    );
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Platform · Reliability</p>
          <h1 className="h-page">Incidents</h1>
          <p className="subhead">
            Every user-visible break in the platform, timeline of updates, and postmortem
            link. Declare one the moment impact is confirmed.
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setDeclaring(true)}>Declare incident</button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          <div className="card kpi"><span className="kpi-label">Open</span>
            <span className="kpi-value">
              {(summary.byStatus.declared ?? 0) + (summary.byStatus.investigating ?? 0)
                + (summary.byStatus.mitigating ?? 0) + (summary.byStatus.monitoring ?? 0)}
            </span>
          </div>
          <div className="card kpi"><span className="kpi-label">SEV1</span>
            <span className="kpi-value">{summary.bySeverity.sev1 ?? 0}</span></div>
          <div className="card kpi"><span className="kpi-label">SEV2</span>
            <span className="kpi-value">{summary.bySeverity.sev2 ?? 0}</span></div>
          <div className="card kpi"><span className="kpi-label">Resolved</span>
            <span className="kpi-value">{summary.byStatus.resolved ?? 0}</span></div>
          <div className="card kpi"><span className="kpi-label">Postmortem</span>
            <span className="kpi-value">{summary.byStatus.postmortem ?? 0}</span></div>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as IncidentStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[IncidentStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={severity} onChange={(e) => setSeverity(e.target.value as IncidentSeverity | '')}>
            <option value="">Any severity</option>
            {(Object.entries(SEVERITY_LABEL) as Array<[IncidentSeverity, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value as IncidentCategory | '')}>
            <option value="">Any category</option>
            {(Object.entries(CATEGORY_LABEL) as Array<[IncidentCategory, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No incidents"
          emptyHint="Nothing broken right now. Widen the filters to see history."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} onClick={() => setOpenId(r._id)} style={{ cursor: 'pointer' }}>
                    <td className="cell-sub">{r.incidentNumber}</td>
                    <td className="cell-title">
                      {r.title}
                      {r.platformWide && <span className="badge ml-2">platform-wide</span>}
                    </td>
                    <td className="cell-sub">{CATEGORY_LABEL[r.category]}</td>
                    <td><span className={`badge ${sevTone(r.severity)}`}>{r.severity.toUpperCase()}</span></td>
                    <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                    <td className="cell-sub">{fmt(r.startedAt)}</td>
                    <td className="cell-sub">{durationText(r.durationMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      {/* Declare drawer */}
      <Drawer open={declaring} onClose={() => setDeclaring(false)} title="Declare incident" size="lg">
        <div className="stack">
          <Notice tone="warning">
            <b>Declare only when user impact is confirmed.</b> The incident number is public
            once you tick "publicly visible". Rehearsals and internal probes stay off.
          </Notice>

          <label className="block">
            <span className="eyebrow">Title (short, factual)</span>
            <input className="input mt-1" value={dTitle} onChange={(e) => setDTitle(e.target.value)}
              placeholder='e.g. "Nexie chat returning 502 for TPO shell"' />
          </label>

          <div className="grid gap-3 md:grid-cols-3">
            <label className="block">
              <span className="eyebrow">Category</span>
              <select className="select mt-1" value={dCategory} onChange={(e) => setDCategory(e.target.value as IncidentCategory)}>
                {(Object.entries(CATEGORY_LABEL) as Array<[IncidentCategory, string]>).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="eyebrow">Severity</span>
              <select className="select mt-1" value={dSeverity} onChange={(e) => setDSeverity(e.target.value as IncidentSeverity)}>
                {(Object.entries(SEVERITY_LABEL) as Array<[IncidentSeverity, string]>).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="eyebrow">Affected services (comma-sep)</span>
              <input className="input mt-1" value={dServices} onChange={(e) => setDServices(e.target.value)}
                placeholder="api, nexie, notifications" />
            </label>
          </div>

          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dPlatformWide} onChange={(e) => setDPlatformWide(e.target.checked)} />
              <span>Platform-wide (all tenants affected)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dPublic} onChange={(e) => setDPublic(e.target.checked)} />
              <span>Publicly visible on status banner</span>
            </label>
          </div>

          <label className="block">
            <span className="eyebrow">Initial message (min 5 chars)</span>
            <textarea className="input mt-1" rows={4} value={dMessage} onChange={(e) => setDMessage(e.target.value)}
              placeholder="What we see, what we're doing, what users should expect." />
          </label>

          <div className="flex justify-end gap-2">
            <button className="btn btn-ghost" onClick={() => setDeclaring(false)}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={dTitle.trim().length < 5 || dMessage.trim().length < 5 || declare.isPending}
              onClick={submitDeclare}
            >
              {declare.isPending ? 'Declaring…' : 'Declare incident'}
            </button>
          </div>
          {declare.error && <Notice tone="warning">Failed: {String((declare.error as Error).message)}</Notice>}
        </div>
      </Drawer>

      {/* Detail drawer */}
      <Drawer open={Boolean(openId)} onClose={() => setOpenId(null)} title={detail.data?.incidentNumber ?? 'Incident'} size="lg">
        {detail.isLoading && <div className="p-4 cell-sub">Loading…</div>}
        {detail.data && (
          <div className="stack">
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="h-section m-0">{detail.data.title}</h2>
                <span className={`badge ${sevTone(detail.data.severity)}`}>{detail.data.severity.toUpperCase()}</span>
                <span className={`badge ${statusTone(detail.data.status)}`}>{STATUS_LABEL[detail.data.status]}</span>
              </div>
              <p className="cell-sub m-0 mt-1">
                {CATEGORY_LABEL[detail.data.category]} · started {fmt(detail.data.startedAt)}
                {detail.data.resolvedAt && ` · resolved ${fmt(detail.data.resolvedAt)}`}
                {detail.data.durationMinutes !== undefined && ` · ${durationText(detail.data.durationMinutes)}`}
              </p>
            </div>

            {detail.data.affectedServices && detail.data.affectedServices.length > 0 && (
              <div className="card card-pad">
                <div className="eyebrow mb-1">Affected services</div>
                <div className="flex flex-wrap gap-1">
                  {detail.data.affectedServices.map((s) => <span key={s} className="qtype">{s}</span>)}
                </div>
              </div>
            )}

            {detail.data.rootCause && (
              <div className="card card-pad">
                <div className="eyebrow">Root cause</div>
                <p className="m-0 mt-1 whitespace-pre-line">{detail.data.rootCause}</p>
              </div>
            )}

            {detail.data.postmortemUrl && (
              <div className="card card-pad">
                <div className="eyebrow">Postmortem</div>
                <a className="link" href={detail.data.postmortemUrl} target="_blank" rel="noreferrer">
                  {detail.data.postmortemUrl}
                </a>
              </div>
            )}

            <div className="card card-pad">
              <div className="eyebrow mb-2">Timeline</div>
              <ol className="stack">
                {(detail.data.updates ?? []).map((u, i) => (
                  <li key={i} className="border-l-2 pl-3 py-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`badge ${statusTone(u.status)}`}>{STATUS_LABEL[u.status]}</span>
                      <span className="cell-sub">{fmt(u.at)}</span>
                    </div>
                    <p className="m-0 mt-1 whitespace-pre-line">{u.message}</p>
                  </li>
                ))}
              </ol>
            </div>

            {detail.data.status !== 'postmortem' && (
              <div className="card card-pad">
                <div className="eyebrow mb-2">Post an update</div>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="eyebrow">New status</span>
                    <select className="select mt-1" value={uStatus} onChange={(e) => setUStatus(e.target.value as IncidentStatus)}>
                      {(Object.entries(STATUS_LABEL) as Array<[IncidentStatus, string]>).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block mt-3">
                  <span className="eyebrow">Message</span>
                  <textarea className="input mt-1" rows={3} value={uMessage} onChange={(e) => setUMessage(e.target.value)}
                    placeholder="What changed since the last update." />
                </label>
                {(uStatus === 'resolved' || uStatus === 'postmortem') && (
                  <label className="block mt-3">
                    <span className="eyebrow">Root cause (recommended on resolve)</span>
                    <textarea className="input mt-1" rows={3} value={uRootCause} onChange={(e) => setURootCause(e.target.value)} />
                  </label>
                )}
                {uStatus === 'postmortem' && (
                  <label className="block mt-3">
                    <span className="eyebrow">Postmortem doc URL</span>
                    <input className="input mt-1" value={uPostmortemUrl} onChange={(e) => setUPostmortemUrl(e.target.value)}
                      placeholder="https://..." />
                  </label>
                )}
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    className="btn btn-primary"
                    disabled={uMessage.trim().length < 5 || postUpdate.isPending}
                    onClick={submitUpdate}
                  >
                    {postUpdate.isPending ? 'Posting…' : 'Post update'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
